import React, { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';
import { Spinner } from './Spinner';

/**
 * `common/RowAction` (M-A, architecture-ux-density-v2.md §5.3).
 *
 * The compact table-row action button — replaces the ~dozen hand-rolled
 * `text-xs px-3 py-1` mini-buttons and icon-links scattered across the list
 * pages (Archive/OpenItems/Customers/Products). Always a native
 * `<button type="button">` with a WCAG 2.5.8 hit target (≥ 32×32 px via
 * padding, even in icon-only mode).
 *
 * Accessibility is enforced by the API, not left to callers: `label` is
 * mandatory and becomes `aria-label` + `title` (hover tooltip). In icon-only
 * mode that is the ONLY accessible name, so making `label` required guarantees
 * the icon button is never nameless. The `icon` itself is `aria-hidden`.
 */
export interface RowActionProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'title'> {
  /** Pflicht: zugängliches Label. Wird aria-label + title (Tooltip) + ggf. sichtbarer Text. */
  label: string;
  icon: ReactNode;
  /** 'icon' (nur Icon, Label als Tooltip/aria) | 'labeled' (Icon + sichtbarer Kurztext). Default 'icon'. */
  appearance?: 'icon' | 'labeled';
  variant?: 'default' | 'accent' | 'danger';
  loading?: boolean;
}

const VARIANT_CLASSES: Record<NonNullable<RowActionProps['variant']>, string> = {
  default: 'text-secondary hover:text-primary hover:bg-surface-inset',
  accent: 'text-accent-ink hover:text-accent-ink hover:bg-surface-inset',
  danger: 'text-[var(--tone-danger-fg)] hover:text-[var(--tone-danger-fg)] hover:bg-[var(--tone-danger-bg)]',
};

export const RowAction = forwardRef<HTMLButtonElement, RowActionProps>(function RowAction(
  {
    label,
    icon,
    appearance = 'icon',
    variant = 'default',
    loading = false,
    disabled,
    className = '',
    type,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading;
  const isIconOnly = appearance === 'icon';

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      // aria-label + title are ALWAYS present (icon-only mode has no visible text).
      aria-label={label}
      title={label}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center gap-1 rounded-control text-xs font-medium',
        'transition-colors duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        // WCAG 2.5.8: keep the tap target ≥ 32×32 even for an icon-only button.
        isIconOnly ? 'min-h-[32px] min-w-[32px] p-1.5' : 'min-h-[32px] px-2 py-1',
        VARIANT_CLASSES[variant],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {loading ? (
        <Spinner size="sm" />
      ) : (
        <span className="shrink-0 inline-flex" aria-hidden="true">
          {icon}
        </span>
      )}
      {appearance === 'labeled' && !loading ? <span>{label}</span> : null}
    </button>
  );
});

export default RowAction;
